/**
 * NewLanguageDialog.tsx — 新增语言版本（books 域，ADM-BOK-S006）· 已接真
 *
 * 接真：`POST /api/v1/book-groups/:groupId/languages`（booksApi.createLanguage，A6 §17）。
 * **`:id` 是书组 id（`novel_groups.id`，dd#970），不再是「书组根 novel_id」** —— root 可空后
 * 根行不能代表书组。在该书组下 INSERT 一行 novels（`group_id = :id`）——category / tags /
 * age_rating 缺省以书组代表行当前值初始化，创建后可按语言独立编辑；`original_*` 与权属 `source`
 * 在 `novel_groups` 上一份，不是本端点入参。
 *
 * 改写版本标签（ADM-BOK-S024，可选）：**不继承**同组其他语言版本的标签，初值恒空（= 原版）；trim 后非空才
 * 下发。新行 source_provider 恒 NULL（§17 不收 provider 两字段），成员判定仍经共用单点求值。
 *
 * cover_url 复用代表行封面默认值（同书不同语言、同封面；无 admin R2 直传通道，故不上传、直接沿用
 * 既有 CDN URL，可编辑）。slug 不属于 §17 入参，由服务端继承同组（同 group 共享 slug ownership）。
 */

import type { NovelResponse } from '@novel-admin/shared';
import {
  EDITION_LABEL_MAX_LENGTH,
  isTranslationFactoryProviderCode,
  parseEditionLabelDraft,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { BookRow } from '~/data/mock/types';
import { booksApi } from '~/hooks/use-books';
import { fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';

export function NewLanguageDialog({
  book,
  representativeRow,
  availableLocales,
  onClose,
}: {
  book: BookRow;
  /** 书组代表行（继承 cover_url / slug / 简介 默认值；A6 §15.1「书组代表行」）*/
  representativeRow: NovelResponse | null;
  /** 可新增的语言 code 列表（已启用语言 − 已有版本）；zh 显示名可空 → 回退 code */
  availableLocales: Array<{ code: string; zh?: string }>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // §17 的 `:id` 是书组 id（dd#970）
  const groupId = book.groupId;
  const [locale, setLocale] = useState(availableLocales[0]?.code ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState(representativeRow?.description ?? '');
  // cover_url 复用 root 封面（无 R2 直传通道）；可编辑
  const [coverUrl, setCoverUrl] = useState(representativeRow?.cover_url ?? '');
  const [freeChapterCount, setFreeChapterCount] = useState('5');
  // S024：初值恒空，不以代表行 / 同组其他行的标签预填（A6 §17「不继承」）
  const [editionLabel, setEditionLabel] = useState('');
  const editionLabelDraft = parseEditionLabelDraft(editionLabel);
  const editionLabelFollowsBase = isTranslationFactoryProviderCode(null);

  const parsedFreeChapterCount = Number(freeChapterCount);
  const freeChapterCountValid =
    freeChapterCount.trim().length > 0 &&
    Number.isInteger(parsedFreeChapterCount) &&
    parsedFreeChapterCount >= 0 &&
    parsedFreeChapterCount <= 1000;
  const valid =
    !!locale &&
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    /^https?:\/\//.test(coverUrl.trim()) &&
    freeChapterCountValid &&
    editionLabelDraft.ok;

  const create = useMutation({
    mutationFn: () => {
      // Expand/contract：部署顺序固定 Web-first。旧 Worker 仍要求并持久化 slug，因此兼容字段必须
      // 直接沿用同组 slug；新 Worker 按 §17 忽略该字段并继承同一 slug。窗口结束后可移除该键。
      const body = {
        slug: representativeRow?.slug ?? book.id,
        locale,
        title: title.trim(),
        description: description.trim(),
        cover_url: coverUrl.trim(),
        free_chapter_count: parsedFreeChapterCount,
        // 工厂译制平台 + 携带 edition_label 会被 §17 拒：成员不下发；留空 = 原版，不带该键
        ...(!editionLabelFollowsBase && editionLabelDraft.ok && editionLabelDraft.value !== null
          ? { edition_label: editionLabelDraft.value }
          : {}),
      };
      return booksApi.createLanguage(groupId, body);
    },
    onSuccess: async (created) => {
      toast(`已新增 ${fmtLang(locale)} 语言版本`, { tone: 'pos', icon: 'IconCheck' });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['books'] }),
        queryClient.invalidateQueries({ queryKey: ['categories'] }),
        queryClient.invalidateQueries({ queryKey: ['tags'] }),
        queryClient.invalidateQueries({ queryKey: ['book-detail', book.groupId] }),
        queryClient.invalidateQueries({ queryKey: ['book-logs', created.novel_id] }),
      ]);
      onClose();
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '新增失败', { tone: 'neg', icon: 'IconAlert' }),
  });

  return (
    <Dialog
      title="新增语言版本"
      subtitle={`《${book.displayTitle}》· 继承书组分类/标签/年龄分级，仅填本语言文案`}
      icon="IconPlus"
      width={520}
      onClose={onClose}
      footer={
        <div className="hstack" style={{ gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!valid || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? '新增中…' : '新增语言版本'}
          </button>
        </div>
      }
    >
      <div className="vstack" style={{ gap: 12, padding: 16 }}>
        <FormRow label="语言" required>
          <Select
            className="input"
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            {availableLocales.map((l) => (
              <option key={l.code} value={l.code}>
                {l.zh ?? l.code}（{l.code}）
              </option>
            ))}
          </Select>
        </FormRow>
        <FormRow label="标题" required>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="该语言版本书名"
          />
        </FormRow>
        <FormRow label="简介" required>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="该语言版本简介"
          />
        </FormRow>
        <FormRow
          label="封面 URL"
          required
          hint="默认沿用书组封面（同书同封面）；如需不同封面填入 CDN URL"
        >
          <input
            className="input mono"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            placeholder="https://cdn.example.com/..."
            style={{ fontSize: 12 }}
          />
        </FormRow>
        <FormRow label="改写版本" hint="不继承其他语言版本的标签 · 留空 = 原版 · 创建后可修改">
          <div className="hstack" style={{ gap: 8, alignItems: 'center' }}>
            <input
              aria-label="改写版本"
              className="input"
              value={editionLabel}
              onChange={(e) => setEditionLabel(e.target.value)}
              placeholder="留空 = 原版"
              disabled={editionLabelFollowsBase}
              style={{ maxWidth: 200 }}
            />
            {!editionLabelDraft.ok && (
              <span style={{ fontSize: 11, color: 'var(--neg)' }}>
                最多 {EDITION_LABEL_MAX_LENGTH} 个字符
              </span>
            )}
          </div>
        </FormRow>
        <FormRow label="免费章节数" required hint="范围 0–1000；创建后可在书籍信息中调整">
          <input
            aria-label="免费章节数"
            className="input mono"
            type="number"
            min={0}
            max={1000}
            step={1}
            value={freeChapterCount}
            onChange={(e) => setFreeChapterCount(e.target.value)}
            style={{ maxWidth: 160 }}
          />
        </FormRow>
        <div className="hstack" style={{ gap: 5, fontSize: 11, color: 'var(--fg-muted)' }}>
          <Icons.IconInfo size={12} style={{ color: 'var(--info)', flexShrink: 0 }} />
          分类 / 标签 / 年龄分级将按 root
          当前值初始化，创建后可在该语言版本中独立编辑；原作与采集来源仍由 root 维护。
        </div>
      </div>
    </Dialog>
  );
}
